/**
 * App Component - Root of the React Application
 *
 * This component:
 * 1. Fetches the health status from the Worker API
 * 2. Displays connection status
 * 3. Shows the basic app shell
 *
 * The health check proves that:
 * - Vite's proxy is routing /api to the Worker
 * - The Worker is responding correctly
 */

import { useState, useEffect } from 'react';
import { HealthResponse } from '@deep-agents/shared-types';
import './App.css';

/**
 * Status type for the health check.
 */
type ConnectionStatus = 'loading' | 'connected' | 'error';

/**
 * Main App component.
 */
function App() {
  // Track API health status
  const [status, setStatus] = useState<ConnectionStatus>('loading');
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch health on mount
  useEffect(() => {
    async function checkHealth() {
      try {
        // In production: use VITE_API_URL from environment
        // In development: use Vite proxy (empty string uses relative path)
        const apiUrl = import.meta.env.VITE_API_URL || '';

        // Fetch from /api/health
        // In development, Vite proxies this to http://localhost:8787
        // In production, apiUrl points to deployed Worker
        const response = await fetch(`${apiUrl}/api/health`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data: HealthResponse = await response.json();
        setHealth(data);
        setStatus('connected');
      } catch (err) {
        console.error('Health check failed:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    }

    checkHealth();

    // Optional: Re-check every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <h1>Deep Agents</h1>
        <p className="tagline">AI Collaborative Workspace</p>
      </header>

      {/* Main content */}
      <main className="app-main">
        {/* Connection status card */}
        <div className="status-card">
          <h2>API Connection Status</h2>

          {status === 'loading' && (
            <div className="status status-loading">
              <span className="status-indicator">●</span>
              Connecting to API...
            </div>
          )}

          {status === 'connected' && health && (
            <div className="status status-connected">
              <span className="status-indicator">●</span>
              Connected
              <div className="status-details">
                <p><strong>Status:</strong> {health.status}</p>
                <p><strong>Environment:</strong> {health.environment}</p>
                <p><strong>Timestamp:</strong> {new Date(health.timestamp).toLocaleString()}</p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="status status-error">
              <span className="status-indicator">●</span>
              Connection Failed
              <div className="status-details">
                <p>{error}</p>
                <p className="hint">
                  Make sure the Worker is running: <code>cd apps/worker && pnpm dev</code>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Next steps info */}
        <div className="info-card">
          <h2>Phase 1: Foundation</h2>
          <p>
            This is the foundation of the Deep Agents platform. The infrastructure is set up:
          </p>
          <ul>
            <li>✅ Cloudflare Worker API (backend)</li>
            <li>✅ React Frontend (this page)</li>
            <li>✅ D1 Database (metadata storage)</li>
            <li>✅ R2 Bucket (file storage)</li>
            <li>✅ Durable Objects (board state)</li>
            <li>✅ Multi-tenant security (in middleware)</li>
          </ul>
          <p>
            Next phases will add authentication, real-time collaboration,
            the whiteboard canvas, and AI features.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <p>Deep Agents Platform - Phase 1 Foundation</p>
      </footer>
    </div>
  );
}

export default App;
